import { useEffect, useState, useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Check, Info, Pause, X } from 'lucide-react';
import {
  ApiError,
  api,
  isJobInProgressBody,
  type JobInProgressConflict,
  type SalesOrderSummary,
  type UpdateSalesOrderBody,
} from '../lib/api.js';
import { Feedback } from '../components/ui/feedback.js';
import { CustomerPicker } from '../components/customer-picker.js';
import { ConfirmDialog } from '../components/ui/confirm-dialog.js';
import { useFieldJobsSettings, useSchedulingSettings } from '../lib/settings.js';
import { shiftIsoDate, useApplyCompanyDayWindow } from '../lib/dispatch-board.js';
import { offDuring, unavailability, useAvailabilityWindow, whoIsOut } from '../lib/availability.js';
import { Input } from '../components/ui/input.js';
import { Pill } from '../components/ui/pill.js';
import { cn } from '../lib/cn.js';
import { useVisitMutations, useVisitsWindow, visitStartHour } from '../lib/visits.js';
import { orderTrade, useSalesTypes, type Trade } from '../lib/trade.js';
import type { Unavailable } from '../lib/availability.js';
import {
  absoluteHourLabel,
  assignmentUdfPatch,
  formatStoredStart,
  helpersUdfPatch,
  moveDateUdfPatch,
  readAssignment,
  resizeUdfPatch,
  readCompletedAt,
  readHelpers,
  unassignUdfPatch,
  useDispatchBoard,
  useTechs,
  type Assignment,
  type Technician,
} from '../lib/dispatch-board.js';
import { KpiStrip } from '../components/dispatch/kpi-strip.js';
import { FilterBar } from '../components/dispatch/filter-bar.js';
import { QueueRail } from '../components/dispatch/queue-rail.js';
import { BoardGrid } from '../components/dispatch/board-grid.js';
import { DetailPanel } from '../components/dispatch/detail-panel.js';
import { ActivityFeed } from '../components/dispatch/activity-feed.js';
import { MobileDispatch } from '../components/dispatch/mobile-dispatch.js';
import { BoardDatePicker } from '../components/dispatch/board-date-picker.js';
import { WorkdayPicker } from '../components/dispatch/workday-picker.js';

/**
 * Operations screen — combines the dispatch board and the sales-orders list.
 *
 * Inbox (left): all orders + quotes that match the current Status / Trade /
 * Search filter, minus those already placed on the timeline. Drag a card to
 * a tech-and-hour slot to assign.
 * Board (right): timeline for the selected day, populated with placed cards.
 *
 * Assignments persist as `FOP_TECH` / `FOP_DATE` / `FOP_START` /
 * `FOP_DURATION` UDF keys on the order header (see CLAUDE.md UDF rules).
 * Writes go through `salesOrders.update` (PUT) with a `udf` patch; Spire
 * merges those keys without disturbing the customer's own UDF.
 *
 * Optimistic UX: assign/unassign mutate the TanStack Query cache immediately
 * then revalidate on settle. A failed PUT reverts via the refetch.
 *
 * Routes `/dispatch` and `/sales-orders` both render this screen
 * (redirected at router level) so existing bookmarks keep working.
 */
type OperationsSearch = {
  status?: 'open' | 'quotes' | 'invoiced';
  customerNo?: string;
  orderNo?: string;
};

export function DispatchScreen() {
  const queryClient = useQueryClient();
  const filter = useDispatchBoard((s) => s.filter);
  const selectedDate = useDispatchBoard((s) => s.selectedDate);
  const scheduling = useSchedulingSettings();
  const salesTypes = useSalesTypes();
  // The company's day window is the board's default view; a dispatcher's
  // own localStorage override (WorkdayPicker) still wins once they've set one.
  useApplyCompanyDayWindow(scheduling);
  const pushActivity = useDispatchBoard((s) => s.pushActivity);
  const techsQuery = useTechs();
  const techs = techsQuery.data ?? [];
  const fieldJobs = useFieldJobsSettings();
  // Availability for the selected day: time off + holidays fetched for a
  // window around it so paging a day or two doesn't refetch.
  const availWindow = useAvailabilityWindow(shiftIsoDate(selectedDate, -7), shiftIsoDate(selectedDate, 7));
  const unavailable = useMemo(() => {
    const m = new Map<string, Unavailable>();
    for (const t of techs) {
      const why = unavailability(t, selectedDate, availWindow.timeOff.filter((o) => o.techId === t.id), availWindow.holidays);
      if (why) m.set(t.id, why);
    }
    return m;
  }, [techs, selectedDate, availWindow.timeOff, availWindow.holidays]);
  const outToday = useMemo(
    () => whoIsOut(techs, selectedDate, availWindow.timeOff, availWindow.holidays),
    [techs, selectedDate, availWindow.timeOff, availWindow.holidays],
  );

  // URL is the source of truth for Status / Customer / Order # filters.
  // Trade and selected date stay in the dispatch-board zustand store so the
  // toolbar UX is unchanged.
  const search = useSearch({ strict: false }) as OperationsSearch;
  const navigate = useNavigate();
  const statusView = search.status ?? 'open';
  const customerNo = search.customerNo ?? '';
  const orderNo = search.orderNo ?? '';

  const [draggingTrade, setDraggingTrade] = useState<Trade | null>(null);
  const [customerNoDraft, setCustomerNoDraft] = useState(customerNo);
  const [orderNoDraft, setOrderNoDraft] = useState(orderNo);

  // Debounce search input edits into the URL so the query key (and history
  // entry) only updates when the dispatcher pauses typing.
  useEffect(() => {
    if (customerNoDraft === customerNo && orderNoDraft === orderNo) return;
    const handle = setTimeout(() => {
      void navigate({
        to: '/operations',
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          customerNo: customerNoDraft || undefined,
          orderNo: orderNoDraft || undefined,
        }),
      });
    }, 250);
    return () => clearTimeout(handle);
  }, [customerNoDraft, orderNoDraft, customerNo, orderNo, navigate]);

  // Translate the user-facing status chip into Spire-side status/type pair.
  // Mirrors `VIEWS` in the old sales-orders-search.tsx for URL compatibility.
  const spireParams = statusToSpireFilter(statusView);

  // Status=Open intentionally has NO type filter so quotes (type=Q) AND
  // work orders (type=W) both surface in the inbox. The card's pill tells
  // them apart and the dispatcher can choose to schedule either.
  const ordersKey = [
    'operations',
    'list',
    statusView,
    customerNo || null,
    orderNo || null,
  ] as const;
  const ordersQuery = useQuery({
    queryKey: ordersKey,
    queryFn: ({ signal }) =>
      api.salesOrders.search(
        {
          ...spireParams,
          customerNo: customerNo || undefined,
          orderNo: orderNo || undefined,
          limit: 500,
          // Only the schedulable views need UDF enrichment for board
          // placement. Invoiced is read-only — skip the N-extra-fetches.
          includeUdf: statusView !== 'invoiced',
        },
        signal,
      ),
    staleTime: 25_000,
    // The board is the office's shared source of truth: without polling a
    // dispatcher stares at a page-load-time snapshot all morning (orders
    // created in Spire desktop and field phase changes never appear).
    // 30s matches the Spire sync cadence for sales orders.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    // Keep the previous page on filter/search changes so every keystroke
    // doesn't flash a false "No matching unassigned work" empty state.
    placeholderData: keepPreviousData,
  });

  const orders = ordersQuery.data ?? [];
  const isSchedulable = statusView !== 'invoiced';

  const updateMutation = useMutation<
    SalesOrderSummary,
    ApiError,
    { spireId: number; patch: UpdateSalesOrderBody }
  >({
    mutationFn: async ({ spireId, patch }) => {
      const updated = await api.salesOrders.update(spireId, patch);
      // The detail PUT returns a SalesOrder; we only need the summary fields
      // for cache writeback. Both are kept in sync below.
      queryClient.setQueryData(['sales-order', spireId], updated);
      return updated as unknown as SalesOrderSummary;
    },
    onMutate: async ({ spireId, patch }) => {
      // Optimistically patch the list cache so the drag feels instant.
      await queryClient.cancelQueries({ queryKey: ordersKey });
      const previous = queryClient.getQueryData<SalesOrderSummary[]>(ordersKey);
      if (previous) {
        const next = previous.map((o) =>
          o.spireId === spireId ? { ...o, udf: { ...o.udf, ...(patch.udf ?? {}) } } : o,
        );
        queryClient.setQueryData(ordersKey, next);
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      // Rollback on failure so the queue/board reflects truth.
      const previous = (ctx as { previous?: SalesOrderSummary[] } | undefined)?.previous;
      if (previous) queryClient.setQueryData(ordersKey, previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ordersKey });
    },
  });

  // Split orders into queue vs placed using the FOP_* UDFs on each order.
  // `placed` is filtered to the currently-selected day; `placedOtherDays`
  // counts items scheduled on other days so the date picker can show
  // "+N other days" without surfacing the rows themselves. `completedCount`
  // is a subset of `placed.length` — completed cards still render on the
  // board (with the Done treatment) so the dispatcher sees what got done
  // vs what's still in progress.
  const { queue, placed, placedForBoard, placedOtherDays, completedCount, scheduledHours } = useMemo(() => {
    const queue: Array<{ order: SalesOrderSummary; trade: Trade }> = [];
    const placed: Array<{
      order: SalesOrderSummary;
      trade: Trade;
      techId: string;
      date: string;
      startHour: number;
      duration: number;
    }> = [];
    // The board needs one entry per (tech, role) so helper techs see a
    // ghost copy of the job on their row. The route-level `placed` stays
    // lead-only so DetailPanel / KpiStrip / drag handlers don't have to
    // care about role multiplicity. We expand here and pass the wider
    // set into BoardGrid only.
    const placedForBoard: Array<{
      order: SalesOrderSummary;
      trade: Trade;
      techId: string;
      date: string;
      startHour: number;
      duration: number;
      role: 'lead' | 'helper';
    }> = [];
    let placedOtherDays = 0;
    let completedCount = 0;
    let scheduledHours = 0;
    for (const order of orders) {
      const trade = orderTrade(salesTypes, order, fieldJobs.typeUdfKey);
      const a = readAssignment(order.udf ?? {});
      if (!a) {
        queue.push({ order, trade });
        continue;
      }
      if (a.date !== selectedDate) {
        placedOtherDays += 1;
        continue;
      }
      if (readCompletedAt(order.udf ?? {})) completedCount += 1;
      scheduledHours += a.duration;
      placed.push({
        order,
        trade,
        techId: a.techId,
        date: a.date,
        startHour: a.startHour,
        duration: a.duration,
      });
      // Lead entry for the board.
      placedForBoard.push({
        order,
        trade,
        techId: a.techId,
        date: a.date,
        startHour: a.startHour,
        duration: a.duration,
        role: 'lead',
      });
      // One ghost entry per helper so each helper's row shows the same
      // time block. Display-only — drag is lead-only.
      for (const helperId of readHelpers(order.udf ?? {})) {
        placedForBoard.push({
          order,
          trade,
          techId: helperId,
          date: a.date,
          startHour: a.startHour,
          duration: a.duration,
          role: 'helper',
        });
      }
    }
    queue.sort((a, b) =>
      (a.order.requiredDate ?? '9999').localeCompare(b.order.requiredDate ?? '9999'),
    );
    return { queue, placed, placedForBoard, placedOtherDays, completedCount, scheduledHours };
  }, [orders, selectedDate, salesTypes]);

  // Extra visits (multi-day work, return trips) on the selected day. They
  // live in Tinsmith, not on the Spire order, so they're a second source
  // merged onto the board next to the orders' own schedules.
  const visitsQuery = useVisitsWindow(shiftIsoDate(selectedDate, -7), shiftIsoDate(selectedDate, 7));
  const visitMutations = useVisitMutations();
  const { visitsToday, visitBlocks } = useMemo(() => {
    const byId = new Map(orders.map((o) => [o.spireId, o]));
    const visitsToday: Array<{
      visitId: string;
      order: SalesOrderSummary;
      trade: Trade;
      techId: string;
      date: string;
      startHour: number;
      duration: number;
    }> = [];
    const visitBlocks: Array<(typeof placedForBoard)[number] & { visitId: string }> = [];
    for (const v of visitsQuery.data ?? []) {
      if (v.date !== selectedDate) continue;
      // Not in the current order view (filtered by search, or no longer
      // open) — nothing to draw the card from.
      const order = byId.get(v.spireSalesOrderId);
      if (!order) continue;
      const base = {
        visitId: v.id,
        order,
        trade: orderTrade(salesTypes, order, fieldJobs.typeUdfKey),
        date: v.date,
        startHour: visitStartHour(v),
        duration: v.durationHours,
      };
      visitsToday.push({ ...base, techId: v.techId });
      visitBlocks.push({ ...base, techId: v.techId, role: 'lead' });
      for (const helperId of v.helperIds) visitBlocks.push({ ...base, techId: helperId, role: 'helper' });
    }
    return { visitsToday, visitBlocks };
  }, [visitsQuery.data, orders, selectedDate, salesTypes, fieldJobs.typeUdfKey]);
  const boardBlocks = useMemo(() => [...placedForBoard, ...visitBlocks], [placedForBoard, visitBlocks]);

  // Rows for the board = the active roster PLUS anyone who still holds a job
  // on this day but isn't in it (a deactivated tech). Without the extra rows
  // those jobs rendered nowhere — no row to sit on, and not in the queue
  // because they're assigned — so deactivating a tech who was going on
  // holiday made their whole week vanish (TFS, 2026-10-10).
  const offRosterIds = useMemo(() => {
    if (!techsQuery.isSuccess) return [];
    const known = new Set(techs.map((t) => t.id));
    return [...new Set(boardBlocks.map((p) => p.techId))].filter((id) => !known.has(id));
  }, [techsQuery.isSuccess, techs, boardBlocks]);
  // Same key as DispatchCard's full-roster query, so it's usually cached.
  const allTechsQuery = useQuery({
    queryKey: ['admin', 'techs', { active: false }] as const,
    queryFn: ({ signal }) => api.admin.techs.list(true, signal),
    staleTime: 60_000,
    enabled: offRosterIds.length > 0,
  });
  const boardTechs = useMemo(() => {
    if (offRosterIds.length === 0) return techs;
    const all = allTechsQuery.data ?? [];
    const extra = offRosterIds.map(
      (id): Technician =>
        all.find((t) => t.id === id) ?? {
          id,
          name: 'Unknown tech',
          initials: '?',
          avatar: 'service',
          trades: [],
          active: false,
          workDays: [],
          warehouseCode: null,
          createdAt: '',
          modifiedAt: '',
        },
    );
    return [...techs, ...extra];
  }, [techs, offRosterIds, allTechsQuery.data]);

  const filteredQueue = useMemo(
    () => queue.filter(({ trade }) => filter === 'all' || trade === filter),
    [queue, filter],
  );

  const handleAssign = (
    orderId: number,
    techId: string,
    startHour: number,
    /** When set, this is a move of an already-placed card — preserve its
     * duration + date. When undefined, it's a fresh drop from the queue —
     * pick the default duration on the dispatcher's currently-selected day. */
    sourceSlot?: number,
    /** Set when the dragged block was an EXTRA visit: move that visit
     * (tech + start time), leaving the order's own schedule alone. */
    visitId?: string,
  ) => {
    const target = orders.find((o) => o.spireId === orderId);
    if (!target) return;
    if (visitId) {
      setDraggingTrade(null);
      const visit = visitsQuery.data?.find((v) => v.id === visitId);
      if (!visit || (visit.techId === techId && visitStartHour(visit) === startHour)) return;
      visitMutations.update.mutate(
        { id: visitId, patch: { techId, startTime: formatStoredStart(startHour) } },
        {
          onError: (err) =>
            pushActivity({ tone: 'danger', text: `Couldn't move the extra visit on ${target.orderNo}: ${err.message}` }),
        },
      );
      pushActivity({
        tone: 'info',
        text: `Moved the extra visit on ${target.orderNo} → ${techs.find((t) => t.id === techId)?.name ?? 'Tech'} at ${absoluteHourLabel(startHour)}`,
      });
      return;
    }
    const existing = readAssignment(target.udf ?? {});
    // If the drop is on the exact slot the card already occupies, no-op
    // (saves a Spire round-trip on accidental drag-and-release).
    if (
      sourceSlot !== undefined &&
      existing &&
      existing.techId === techId &&
      existing.startHour === startHour
    ) {
      setDraggingTrade(null);
      return;
    }
    const trade = orderTrade(salesTypes, target, fieldJobs.typeUdfKey);
    const tech = techs.find((t) => t.id === techId);
    const skillMismatch = tech !== undefined && !tech.trades.includes(trade as never);
    // Soft constraint (agreed 2026-09-23): a drop on an unavailable tech is
    // allowed — the slot warned amber — but the activity feed records it.
    const isMove = sourceSlot !== undefined && existing !== null;
    const assignment: Assignment = {
      techId,
      // Moves preserve the card's existing date so a drag inside the
      // board doesn't accidentally re-target the day picker's day. Fresh
      // drops land on whichever day the board is currently showing.
      date: isMove ? existing.date : selectedDate,
      startHour,
      // Moves preserve duration so a 4h install stays 4h. Fresh drops
      // pick the company default (Admin → Settings) — the dispatcher can
      // grow via the DetailPanel stepper after the drop.
      duration: isMove ? existing.duration : scheduling.defaultJobHours,
    };
    // Part-day time off only counts when the job's hours actually overlap it.
    const off = unavailable.get(techId);
    const offReason = off && offDuring(off, assignment.startHour, assignment.duration) ? off.label : null;
    // Scheduling a QUOTE is a commitment: promote it to a work order in
    // the same PUT (type W), otherwise it stays type=Q in Spire forever
    // and never invoices (audit P0-5; Q→W PUT verified against spirelive
    // via packages/spire-client/scripts/verify-quote-promote.ts).
    const promoteQuote = existing === null && target.type === 'quote';
    updateMutation.mutate({
      spireId: orderId,
      // Fresh placements reset any leftover FOP_PHASE; moves of an
      // in-flight job preserve it (the tech may be mid-visit).
      patch: {
        udf: assignmentUdfPatch(assignment, { resetPhase: existing === null }),
        ...(promoteQuote ? { type: 'W' as const } : {}),
      },
    });
    pushActivity({
      tone: skillMismatch ? 'warning' : 'info',
      text: isMove
        ? `Moved ${target.orderNo} → ${tech?.name ?? 'Tech'} at ${absoluteHourLabel(startHour)}${skillMismatch ? ' (skill mismatch)' : ''}`
        : `${tech?.name ?? 'Tech'} assigned to ${target.orderNo} on ${shortDate(selectedDate)}${promoteQuote ? ' — quote promoted to work order' : ''}${skillMismatch ? ' (skill mismatch — overridden)' : ''}${offReason ? ` (${tech?.name ?? 'tech'} is ${offReason.toLowerCase()} — overridden)` : ''}`,
    });
    setDraggingTrade(null);
  };

  // Unschedule. The API refuses (409 JOB_IN_PROGRESS) when a tech is on the
  // job or has completed it; we then show who/when and let the dispatcher
  // force it. `unassignConflict` holds that pending confirmation.
  const [unassignConflict, setUnassignConflict] = useState<
    { orderId: number; conflict: JobInProgressConflict } | null
  >(null);
  const handleUnassign = (orderId: number, force = false) => {
    const target = orders.find((o) => o.spireId === orderId);
    if (!target) return;
    updateMutation.mutate(
      {
        spireId: orderId,
        patch: { udf: unassignUdfPatch, ...(force ? { force: true } : {}) },
      },
      {
        onSuccess: () => {
          setUnassignConflict(null);
          pushActivity({
            tone: 'warning',
            text: `Unscheduled ${target.orderNo} — returned to queue${force ? ' (overrode in-progress guard)' : ''}`,
          });
        },
        onError: (err) => {
          if (err.status === 409 && isJobInProgressBody(err.body)) {
            setUnassignConflict({ orderId, conflict: err.body });
            return;
          }
          pushActivity({ tone: 'danger', text: `Couldn't unschedule ${target.orderNo}: ${err.message}` });
        },
      },
    );
  };

  // Resize-in-place — same write path as assign, but the dispatcher tweaks
  // start hour or duration after the drop via the detail-panel steppers.
  // Preserves the assignment's existing date so a resize doesn't accidentally
  // move the order to today.
  const handleResize = (orderId: number, startHour: number, duration: number) => {
    const target = orders.find((o) => o.spireId === orderId);
    if (!target) return;
    const existing = readAssignment(target.udf ?? {});
    if (!existing) return;
    updateMutation.mutate({
      spireId: orderId,
      patch: { udf: resizeUdfPatch(startHour, duration) },
    });
  };

  // Set the helpers (FOP_HELPERS) list on the selected order. Single
  // UDF PUT — the patch builder wraps the ids in comma boundaries so the
  // server-side $contains filter for "is this tech a helper?" stays
  // unambiguous (no `tech-1` matching `tech-12`).
  const handleSetHelpers = (orderId: number, helperIds: ReadonlyArray<string>) => {
    const target = orders.find((o) => o.spireId === orderId);
    if (!target) return;
    updateMutation.mutate({
      spireId: orderId,
      patch: { udf: helpersUdfPatch(helperIds) },
    });
    pushActivity({
      tone: 'info',
      text:
        helperIds.length === 0
          ? `Cleared helpers on ${target.orderNo}`
          : `Updated helpers on ${target.orderNo} — ${helperIds.length} ${helperIds.length === 1 ? 'helper' : 'helpers'}`,
    });
  };

  // Cross-day reschedule from the DetailPanel — preserves tech / start /
  // duration, just updates FOP_DATE. Clears FOP_COMPLETED_AT so a re-
  // scheduled completed visit becomes a fresh in-progress one.
  const handleMoveDate = (orderId: number, nextDate: string) => {
    const target = orders.find((o) => o.spireId === orderId);
    if (!target) return;
    const existing = readAssignment(target.udf ?? {});
    if (!existing) return;
    if (existing.date === nextDate) return;
    updateMutation.mutate({
      spireId: orderId,
      patch: { udf: moveDateUdfPatch(nextDate) },
    });
    pushActivity({
      tone: 'info',
      text: `Moved ${target.orderNo} → ${shortDate(nextDate)}`,
    });
  };

  const onReset = () => {
    // Bulk unschedule the orders currently visible on this day's board.
    // Other days' assignments are untouched. Each PUT runs independently —
    // they queue through the mutation cache.
    if (placed.length === 0) return;
    const confirmed = window.confirm(
      `Unschedule all ${placed.length} orders on ${shortDate(selectedDate)}? Each one writes a UDF patch back to Spire. Assignments on other days are untouched.`,
    );
    if (!confirmed) return;
    // Jobs a tech has started or completed are left alone: the API's
    // in-progress guard refuses them (409), and a bulk clear is the wrong
    // place to override it. We count the refusals and say so.
    let skipped = 0;
    let done = 0;
    const finish = () => {
      if (done + skipped < placed.length) return;
      pushActivity({
        tone: 'warning',
        text:
          skipped > 0
            ? `Cleared ${done} of ${placed.length} on ${shortDate(selectedDate)} — ${skipped} left in place (a tech has started or completed ${skipped === 1 ? 'it' : 'them'}; unschedule those one at a time)`
            : `Cleared ${done} assignments on ${shortDate(selectedDate)}`,
      });
    };
    for (const p of placed) {
      updateMutation.mutate(
        { spireId: p.order.spireId, patch: { udf: unassignUdfPatch } },
        {
          onSuccess: () => {
            done += 1;
            finish();
          },
          onError: (err) => {
            if (err.status === 409 && isJobInProgressBody(err.body)) skipped += 1;
            else done += 1; // counted so the summary still fires; the rollback already restored the card
            finish();
          },
        },
      );
    }
  };

  return (
    <div
      className="rounded-lg border bg-surface overflow-hidden"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <KpiStrip
        queueCount={queue.length}
        totalOrders={orders.length}
        placedCount={placed.length}
        scheduledHours={scheduledHours}
        completedCount={completedCount}
        techCount={techs.length}
        onReset={onReset}
        dataUpdatedAt={ordersQuery.dataUpdatedAt}
        isFetching={ordersQuery.isFetching}
        onRefresh={() => void ordersQuery.refetch()}
      />
      <StatusBar
        statusView={statusView}
        customerNoDraft={customerNoDraft}
        orderNoDraft={orderNoDraft}
        onStatusChange={(next) =>
          void navigate({
            to: '/operations',
            search: (prev: Record<string, unknown>) => ({
              ...prev,
              status: next === 'open' ? undefined : next,
            }),
          })
        }
        onCustomerNoChange={setCustomerNoDraft}
        onOrderNoChange={setOrderNoDraft}
        onClearSearch={() => {
          setCustomerNoDraft('');
          setOrderNoDraft('');
        }}
        loading={ordersQuery.isFetching}
        resultCount={orders.length}
      />
      {isSchedulable && (
        <BoardDatePicker
          placedOnDay={placed.length + visitsToday.length}
          placedOnOtherDays={placedOtherDays}
        />
      )}
      {isSchedulable && <WorkdayPicker />}
      {isSchedulable && outToday.length > 0 && (
        <div
          className="flex items-center gap-2 flex-wrap px-4 py-1.5 text-[11px] border-b"
          style={{ borderColor: 'var(--color-border-tertiary)', backgroundColor: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-fg)' }}
        >
          <span className="font-medium">
            {outToday.length} {outToday.length === 1 ? 'tech' : 'techs'} out {shortDate(selectedDate)}:
          </span>
          {outToday.map((o) => (
            <span key={o.techId}>
              {o.name} <span className="opacity-80">({o.why.label})</span>
            </span>
          ))}
        </div>
      )}
      <FilterBar />
      {isSchedulable && <SpireOnBoard pending={updateMutation.isPending} />}
      {isSchedulable && <PhaseLegend />}

      {ordersQuery.isError && (
        <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <Feedback variant="error" size="sm">
            Couldn't load open orders: {ordersQuery.error.message}
          </Feedback>
        </div>
      )}

      {updateMutation.isError && (
        <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <Feedback variant="error" size="sm">
            Spire write failed: {updateMutation.error.message}. Last assignment rolled back.
          </Feedback>
        </div>
      )}

      {isSchedulable ? (
        <>
          {/* Desktop: drag-drop timeline. Hidden below md — phones get the
             read-only stack. The wrapper has a DEFINITE height (not just
             max-height) so the rail + board can compute their own internal
             scroll. CSS Grid sizes tracks to content first then overflows
             past max-height, which is why an earlier attempt left the
             queue spilling past the bottom of the page; flex with `h-…`
             gives both children a real height to align to. The 300px
             offset budgets for the KPI / status / date / workday / filter
             / hint / legend stack above; `min-h` guards small viewports. */}
          <div className="hidden md:flex md:h-[calc(100vh-300px)] md:min-h-[420px]">
            <div className="w-[220px] shrink-0 min-h-0">
              <QueueRail
                salesTypes={salesTypes}
                // UNfiltered — the rail applies the trade filter itself, so
                // its "Unassigned (N)" header shows the true backlog and its
                // "N shown" disclosure works (audit: double-filtering made
                // the count lie under any trade filter).
                queue={queue}
                onDragStart={(_orderId, trade) => setDraggingTrade(trade)}
                onDragEnd={() => setDraggingTrade(null)}
                onUnassignDrop={(orderId) => {
                  handleUnassign(orderId);
                  setDraggingTrade(null);
                }}
                onRemoveVisitDrop={(visitId) => {
                  const visit = visitsQuery.data?.find((v) => v.id === visitId);
                  visitMutations.remove.mutate(visitId);
                  if (visit) {
                    pushActivity({ tone: 'info', text: `Removed the extra visit on ${visit.spireOrderNo} (${shortDate(visit.date)})` });
                  }
                  setDraggingTrade(null);
                }}
              />
            </div>
            <div className="flex-1 min-w-0 min-h-0 overflow-y-auto">
              <BoardGrid
                salesTypes={salesTypes}
                techs={boardTechs}
                unavailable={unavailable}
                placed={boardBlocks}
                draggingTrade={draggingTrade}
                onAssign={handleAssign}
              />
            </div>
          </div>

          <MobileDispatch
                salesTypes={salesTypes}
            techs={boardTechs}
            queue={queue}
            placed={[...placed, ...visitsToday]}
            onUnassign={handleUnassign}
          />

          <DetailPanel
                salesTypes={salesTypes}
            techs={techs}
            offRosterTechs={boardTechs.filter((t) => !t.active)}
            placed={placed}
            visitsToday={visitsToday}
            onUnassign={handleUnassign}
            onResize={handleResize}
            onMoveDate={handleMoveDate}
            onSetHelpers={handleSetHelpers}
            onReassignTech={(orderId, techId) => {
              const p = placed.find((x) => x.order.spireId === orderId);
              if (!p) return;
              // Same write path as a board move: passing the current slot
              // as sourceSlot triggers the move branch (preserves
              // date + duration).
              handleAssign(orderId, techId, p.startHour, p.startHour);
            }}
          />
          <ActivityFeed />
          {unassignConflict && (
            <ConfirmDialog
              title={
                unassignConflict.conflict.reason === 'active'
                  ? 'A tech is on this job'
                  : 'This job was completed'
              }
              message={describeConflict(unassignConflict.conflict)}
              confirmLabel="Unschedule anyway"
              pending={updateMutation.isPending}
              onConfirm={() => handleUnassign(unassignConflict.orderId, true)}
              onCancel={() => setUnassignConflict(null)}
            />
          )}
        </>
      ) : (
        // Invoiced view: read-only list, no board. Same inbox-style cards as
        // QueueRail but in a single full-width column. Office staff use this
        // to look up past work without leaving the Operations surface.
        <InvoicedList
          orders={filteredQueue.map(({ order, trade }) => ({ order, trade }))}
          loading={ordersQuery.isPending}
        />
      )}
    </div>
  );
}

/**
 * Map the user-facing status chip to the Spire-side `status`/`type` pair.
 *
 *   open      = status=open → Spire O + L deposit (omit type — orders AND quotes both show)
 *   quotes    = type=Q (any status; usually status=O)
 *   invoiced  = status=P (Spire's posted/invoiced code)
 *
 * Mirrors the VIEWS array from the retired sales-orders-search route so
 * bookmarks with ?status=O&type=W from before the merge still resolve to
 * a sensible default view (the redirect translates them).
 */
function statusToSpireFilter(view: 'open' | 'quotes' | 'invoiced'): {
  status?: string;
  type?: string;
} {
  switch (view) {
    case 'quotes':
      return { type: 'Q' };
    case 'invoiced':
      return { status: 'P' };
    case 'open':
    default:
      // 'open' (not the raw 'O' code) — the API expands it to Spire O + L
      // so deposit-taken orders land on the board too.
      return { status: 'open' };
  }
}

const STATUS_CHIPS: ReadonlyArray<{
  id: 'open' | 'quotes' | 'invoiced';
  label: string;
}> = [
  { id: 'open', label: 'Open' },
  { id: 'quotes', label: 'Quotes' },
  { id: 'invoiced', label: 'Invoiced' },
];

interface StatusBarProps {
  statusView: 'open' | 'quotes' | 'invoiced';
  customerNoDraft: string;
  orderNoDraft: string;
  onStatusChange: (next: 'open' | 'quotes' | 'invoiced') => void;
  onCustomerNoChange: (v: string) => void;
  onOrderNoChange: (v: string) => void;
  onClearSearch: () => void;
  loading: boolean;
  resultCount: number;
}

function StatusBar({
  statusView,
  customerNoDraft,
  orderNoDraft,
  onStatusChange,
  onCustomerNoChange,
  onOrderNoChange,
  onClearSearch,
  loading,
  resultCount,
}: StatusBarProps) {
  const hasSearch = customerNoDraft.length > 0 || orderNoDraft.length > 0;
  return (
    <div
      className="flex items-center gap-2 flex-wrap px-4 py-2 border-b text-xs"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-center gap-1.5">
        {STATUS_CHIPS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={() => onStatusChange(chip.id)}
            aria-pressed={statusView === chip.id}
            className={cn(
              'rounded-full border px-3 py-1 transition-colors',
              statusView === chip.id
                ? 'bg-brand-50 border-brand-200 text-ink-accent font-medium'
                : 'bg-surface border-line-subtle text-ink-muted hover:text-ink',
            )}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1.5 ml-auto">
        <CustomerPicker
          size="sm"
          className="w-44"
          placeholder="Customer name or #"
          value={customerNoDraft}
          onSelect={(c) => onCustomerNoChange(c.customerNo)}
          onClear={() => onCustomerNoChange('')}
        />
        <Input
          aria-label="Order number"
          placeholder="Order # contains…"
          value={orderNoDraft}
          onChange={(e) => onOrderNoChange(e.target.value.trim())}
          className="font-mono h-8 w-36 text-xs"
        />
        {hasSearch && (
          <button
            type="button"
            onClick={onClearSearch}
            aria-label="Clear search"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-ink-muted hover:bg-surface-muted hover:text-ink"
          >
            <X className="h-3 w-3" aria-hidden />
            Clear
          </button>
        )}
        <span className="text-[11px] text-ink-subtle tabular-nums">
          {loading ? 'loading…' : `${resultCount} match${resultCount === 1 ? '' : 'es'}`}
        </span>
      </div>
    </div>
  );
}

interface InvoicedListProps {
  orders: ReadonlyArray<{ order: SalesOrderSummary; trade: Trade }>;
  loading: boolean;
}

function InvoicedList({ orders, loading }: InvoicedListProps) {
  if (loading) {
    return (
      <ul className="p-3 space-y-2" aria-busy="true">
        {Array.from({ length: 6 }).map((_, i) => (
          <li
            key={i}
            className="h-12 rounded-md border bg-surface animate-pulse"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
          />
        ))}
      </ul>
    );
  }
  if (orders.length === 0) {
    return (
      <div className="p-4 text-sm text-ink-muted">
        No invoiced orders match the current filter.
      </div>
    );
  }
  return (
    <ul className="p-3 space-y-2">
      {orders.map(({ order }) => (
        <li key={order.spireId}>
          <Link
            to="/sales-orders/$id"
            params={{ id: String(order.spireId) }}
            className="rounded-md border bg-surface px-3 py-2 text-sm flex items-center gap-3 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
          >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-semibold text-ink">{order.orderNo}</span>
              {order.invoiceNo && (
                <span className="font-mono text-[11px] text-ink-muted">inv {order.invoiceNo}</span>
              )}
              <Pill variant="success">Invoiced</Pill>
            </div>
            <div className="text-xs text-ink truncate mt-0.5">{order.customer.name}</div>
            <div className="text-[11px] text-ink-muted mt-0.5">
              {order.orderDate ?? '—'} · <span className="font-mono">{order.customer.customerNo}</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-ink font-semibold tabular-nums">${order.total}</div>
          </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Plain-language body for the unschedule guard dialog. */
function describeConflict(c: JobInProgressConflict): string {
  const when = c.since
    ? new Date(c.since).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })
    : null;
  if (c.reason === 'active') {
    return `${c.techName ?? 'A tech'} started ${c.orderNo}${when ? ` at ${when}` : ''} and hasn't clocked out. Unscheduling now takes the job off their phone mid-visit; their time stays logged. Ask them to clock out first unless you're sure.`;
  }
  return `${c.orderNo} was marked complete${c.techName ? ` by ${c.techName}` : ''}${when ? ` at ${when}` : ''}. Unscheduling clears that Done mark (time logged stays). Do this only to re-dispatch the job.`;
}

/** Render a YYYY-MM-DD as e.g. "May 28" for activity-feed lines. */
function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Tiny phase-key strip under the SpireOnBoard banner. Decodes the three
 * visual treatments on placed cards (pulse / pause / check) so the
 * dispatcher doesn't have to click a card to figure out what each cue
 * means. Stays read-only — explicitly NOT a control surface.
 */
function PhaseLegend() {
  return (
    <div
      className="flex items-center gap-4 flex-wrap px-4 py-1.5 text-[10.5px] border-b text-ink-muted"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
      aria-label="Card phase legend"
    >
      <span className="font-medium text-ink-subtle uppercase tracking-wider">Card key:</span>
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden
          className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
          style={{ backgroundColor: 'var(--feedback-info-fg)' }}
        />
        In progress
      </span>
      <span className="inline-flex items-center gap-1">
        <Pause className="h-3 w-3" aria-hidden />
        Paused
      </span>
      <span className="inline-flex items-center gap-1">
        <Check className="h-3 w-3 text-emerald-700" aria-hidden />
        Done
      </span>
      <span className="text-ink-subtle">
        Thicker border = tech has started the visit.
      </span>
    </div>
  );
}

function SpireOnBoard({ pending }: { pending: boolean }) {
  return (
    <div
      className="flex items-start gap-2 px-4 py-2 text-[11px] border-b"
      style={{
        borderColor: 'var(--color-border-tertiary)',
        backgroundColor: 'var(--color-customer-card-bg)',
        color: 'var(--color-customer-card-accent)',
      }}
    >
      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
      <span className="flex-1">
        Assignments persist to Spire via <span className="font-mono">FOP_TECH</span> /{' '}
        <span className="font-mono">FOP_DATE</span> /{' '}
        <span className="font-mono">FOP_START</span> /{' '}
        <span className="font-mono">FOP_DURATION</span> UDFs on each order — drops land on the day the picker is currently showing.
      </span>
      {pending && (
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium shrink-0"
          style={{
            backgroundColor: 'var(--pill-sales-bg)',
            color: 'var(--pill-sales-fg)',
          }}
          aria-live="polite"
        >
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: 'var(--trade-sales)' }}
          />
          Writing…
        </span>
      )}
    </div>
  );
}
